import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { copy } from '../i18n/copy';
import type { Mission } from '../data/missions';

interface MissionCardProps {
  mission: Mission;
  selected: boolean;
  onSelect: () => void;
}

export default function MissionCard({ mission, selected, onSelect }: MissionCardProps) {
  const { t } = useLanguage();
  const Icon = mission.icon;

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
      onClick={onSelect}
      className={`group relative flex cursor-pointer flex-col gap-5 overflow-hidden rounded-3xl border bg-navy-900/70 p-5 backdrop-blur-xl transition-[border-color,box-shadow] duration-300 sm:flex-row md:flex-col lg:flex-row ${
        selected
          ? `${mission.borderActive} ${mission.glow}`
          : `border-white/10 ${mission.borderHover}`
      }`}
    >
      {/* Soft accent glow along the top of the card */}
      <div
        className={`pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b ${mission.topGlow} to-transparent opacity-70`}
      />

      {mission.comingSoon && (
        <div
          className={`absolute inset-0 z-10 flex items-center justify-center rounded-3xl bg-navy-950/75 backdrop-blur-[2px]`}
        >
          <span
            className={`rounded-2xl px-6 py-3 text-xl font-extrabold uppercase tracking-widest ring-2 sm:text-2xl ${mission.chip}`}
          >
            {t(copy.comingSoon)}
          </span>
        </div>
      )}

      {/* Content */}
      <div className="relative flex flex-1 flex-col">
        <div className="flex items-center gap-3">
          <span className={`grid size-12 shrink-0 place-items-center rounded-2xl ${mission.iconTile}`}>
            <Icon className="size-6" />
          </span>
          <span>
            <h3 className={`text-xl font-extrabold tracking-wide ${mission.text}`}>
              {t(mission.title)}
            </h3>
            <span className="block text-sm font-medium text-slate-400">{t(mission.subtitle)}</span>
          </span>
        </div>

        <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-300/90">
          {t(mission.description)}
        </p>

        {mission.ctaHref ? (
          <a
            href={mission.ctaHref}
            onClick={(e) => e.stopPropagation()}
            className={`mt-5 inline-flex items-center gap-1.5 self-start rounded-xl px-5 py-2.5 text-sm font-bold transition-all ${mission.button}`}
          >
            {t(mission.cta)}
            <ChevronRight className="size-4 rtl:rotate-180" />
          </a>
        ) : (
          <button
            type="button"
            aria-pressed={selected}
            className={`mt-5 inline-flex items-center gap-1.5 self-start rounded-xl px-5 py-2.5 text-sm font-bold transition-all ${mission.button}`}
          >
            {t(mission.cta)}
            <ChevronRight className="size-4 rtl:rotate-180" />
          </button>
        )}
      </div>

      {/* Artwork */}
      <div className="relative h-40 shrink-0 overflow-hidden rounded-2xl sm:h-auto sm:w-2/5 md:h-44 md:w-auto lg:h-auto lg:w-2/5">
        <img
          src={mission.image}
          alt={t(mission.detail.title)}
          loading="lazy"
          className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-white/10" />
      </div>
    </motion.div>
  );
}
